/* eslint-disable */
import type {
  TaxWebBridgeBackButtonTapEventRequest,
  TaxWebBridgeBackButtonTapEventResponse,
} from "./TaxWebBridgeBackButtonTapEvent.pb";
import type {
  TaxWebBridgeCloseButtonTapEventRequest,
  TaxWebBridgeCloseButtonTapEventResponse,
} from "./TaxWebBridgeCloseButtonTapEvent.pb";
import type {
  TaxWebBridgeConfigureToolbarEventRequest,
  TaxWebBridgeConfigureToolbarEventResponse,
} from "./TaxWebBridgeConfigureToolbarEvent.pb";
import type {
  TaxWebBridgeDesktopButtonTapEventRequest,
  TaxWebBridgeDesktopButtonTapEventResponse,
} from "./TaxWebBridgeDesktopButtonTapEvent.pb";
import type {
  TaxWebBridgeDialogButtonTapEventRequest,
  TaxWebBridgeDialogButtonTapEventResponse,
} from "./TaxWebBridgeDialogButtonTapEvent.pb";
import type { TaxWebBridgeExitEventRequest, TaxWebBridgeExitEventResponse } from "./TaxWebBridgeExitEvent.pb";
import type {
  TaxWebBridgeHelpButtonTapEventRequest,
  TaxWebBridgeHelpButtonTapEventResponse,
} from "./TaxWebBridgeHelpButtonTapEvent.pb";
import type {
  TaxWebBridgeMenuButtonTapEventRequest,
  TaxWebBridgeMenuButtonTapEventResponse,
} from "./TaxWebBridgeMenuButtonTapEvent.pb";
import type {
  TaxWebBridgeMenuItemTapEventRequest,
  TaxWebBridgeMenuItemTapEventResponse,
} from "./TaxWebBridgeMenuItemTapEvent.pb";
import type {
  TaxWebBridgeNativeDeepLinkEventRequest,
  TaxWebBridgeNativeDeepLinkEventResponse,
} from "./TaxWebBridgeNativeDeepLinkEvent.pb";
import type { TaxWebBridgeOpenTaxEventRequest, TaxWebBridgeOpenTaxEventResponse } from "./TaxWebBridgeOpenTaxEvent.pb";
import type {
  TaxWebBridgeShowDialogEventRequest,
  TaxWebBridgeShowDialogEventResponse,
} from "./TaxWebBridgeShowDialogEvent.pb";
import type {
  TaxWebBridgeWebClientReadyEventRequest,
  TaxWebBridgeWebClientReadyEventResponse,
} from "./TaxWebBridgeWebClientReadyEvent.pb";

/** An event wrapper used to pass either a request or response across the web view bridge. */
export interface TaxWebBridgeEvent {
  /**
   * An ID to uniquely identify the event, generated and set by the platform
   * creating the request version of the event.
   */
  id?: string;
  /** Data for a request. */
  request?:
    | TaxWebBridgeRequest
    | undefined;
  /** Data for a response. */
  response?: TaxWebBridgeResponse | undefined;
}

/**
 * Defines a request sent over the web bridge, either from
 * mobile -> web or web -> mobile.
 *
 * Every request sent over the bridge should have a corresponding
 * `TaxWebBridgeResponse` sent back from the opposite platform.
 */
export interface TaxWebBridgeRequest {
  /**
   * The web client is requesting that the web view be closed
   * in order to fully exit Cash App Taxes.
   * Should be sent from the web to mobile.
   */
  exit_event_request?:
    | TaxWebBridgeExitEventRequest
    | undefined;
  /**
   * The hamburger menu button was tapped on the mobile client.
   * Should be sent from mobile to web.
   */
  menu_button_tap_event_request?:
    | TaxWebBridgeMenuButtonTapEventRequest
    | undefined;
  /**
   * An item on the hamburger menu was tapped on the mobile client.
   * Should be sent from mobile to web.
   */
  menu_item_tap_event_request?:
    | TaxWebBridgeMenuItemTapEventRequest
    | undefined;
  /**
   * The web client is requesting that the mobile client display a dialog.
   * Should be sent from web to mobile.
   */
  show_dialog_event_request?:
    | TaxWebBridgeShowDialogEventRequest
    | undefined;
  /**
   * A button on a dialog was tapped on the mobile client.
   * Should be sent from mobile to web.
   */
  dialog_button_tap_event_request?:
    | TaxWebBridgeDialogButtonTapEventRequest
    | undefined;
  /**
   * The web client has been initialized and is prepared to receive mobile events.
   * Should be send from web to mobile.
   */
  web_client_ready_event_request?:
    | TaxWebBridgeWebClientReadyEventRequest
    | undefined;
  /**
   * The back button was tapped on the mobile client.
   * Should be sent from mobile to web.
   */
  back_button_tap_event_request?:
    | TaxWebBridgeBackButtonTapEventRequest
    | undefined;
  /**
   * The web client is requesting that the mobile client call
   * /cash-app/tax/1.0/open-tax to continue the flow.
   * Should be sent from the web to mobile.
   */
  open_tax_event_request?:
    | TaxWebBridgeOpenTaxEventRequest
    | undefined;
  /**
   * The web client is requesting that mobile client configure the
   * buttons present in the toolbar of the webview.
   * Should be sent from the web to mobile.
   */
  configure_toolbar_event_request?:
    | TaxWebBridgeConfigureToolbarEventRequest
    | undefined;
  /**
   * The close button was tapped on the mobile client.
   * Should be sent from mobile to web.
   */
  close_button_tap_event_request?:
    | TaxWebBridgeCloseButtonTapEventRequest
    | undefined;
  /**
   * The help button was tapped on the mobile client.
   * Should be sent from mobile to web.
   */
  help_button_tap_event_request?:
    | TaxWebBridgeHelpButtonTapEventRequest
    | undefined;
  /**
   * Web is asking native client to open one of its deep links.
   * Should be sent from web to mobile.
   */
  native_deep_link_event_request?:
    | TaxWebBridgeNativeDeepLinkEventRequest
    | undefined;
  /**
   * The desktop button was tapped on the mobile client.
   * Should be sent from mobile to web.
   */
  desktop_button_tap_event_request?: TaxWebBridgeDesktopButtonTapEventRequest | undefined;
}

/**
 * Defines a response sent over the web bridge, either from
 * mobile -> web or web -> mobile.
 */
export interface TaxWebBridgeResponse {
  /**
   * The status of the response.
   * Anything other than `SUCCESS` indicates an error.
   */
  status?: TaxWebBridgeResponseStatus;
  /**
   * A response for `TaxWebBridgeExitEventRequest`
   * Should be sent from mobile to web.
   */
  exit_event_response?:
    | TaxWebBridgeExitEventResponse
    | undefined;
  /**
   * A response for `TaxWebBridgeMenuButtonTapEventRequest`
   * Should be sent from web to mobile.
   */
  menu_button_tap_event_response?:
    | TaxWebBridgeMenuButtonTapEventResponse
    | undefined;
  /**
   * A response for `TaxWebBridgeMenuItemTapEventRequest`
   * Should be sent from web to mobile.
   */
  menu_item_tap_event_response?:
    | TaxWebBridgeMenuItemTapEventResponse
    | undefined;
  /**
   * A response for `TaxWebBridgeShowDialogEventRequest`
   * Should be sent from mobile to web.
   */
  show_dialog_event_response?:
    | TaxWebBridgeShowDialogEventResponse
    | undefined;
  /**
   * A response for `TaxWebBridgeDialogButtonTapEventRequest`
   * Should be sent from web to mobile.
   */
  dialog_button_tap_event_response?:
    | TaxWebBridgeDialogButtonTapEventResponse
    | undefined;
  /**
   * A response for `TaxWebBridgeWebClientReadyEventRequest`
   * Should be sent from mobile to web.
   */
  web_client_ready_event_response?:
    | TaxWebBridgeWebClientReadyEventResponse
    | undefined;
  /**
   * A response for `TaxWebBridgeBackButtonTapEventRequest `
   * Should be sent from web to mobile.
   */
  back_button_tap_event_response?:
    | TaxWebBridgeBackButtonTapEventResponse
    | undefined;
  /**
   * A response for `TaxWebBridgeOpenTaxEventRequest `
   * Should be sent from mobile to web.
   */
  open_tax_event_response?:
    | TaxWebBridgeOpenTaxEventResponse
    | undefined;
  /**
   * A response for `TaxWebBridgeConfigureToolbarEventRequest` containing a list
   * of IDs that could not be set.
   * Should be sent from mobile to web.
   */
  configure_toolbar_event_response?:
    | TaxWebBridgeConfigureToolbarEventResponse
    | undefined;
  /**
   * A response for `TaxWebBridgeCloseButtonTapEventRequest `
   * Should be sent from web to mobile.
   */
  close_button_tap_event_response?:
    | TaxWebBridgeCloseButtonTapEventResponse
    | undefined;
  /**
   * A response for `TaxWebBridgeHelpButtonTapEventRequest `
   * Should be sent from web to mobile.
   */
  help_button_tap_event_response?:
    | TaxWebBridgeHelpButtonTapEventResponse
    | undefined;
  /**
   * A response for `TaxWebBridgeNativeDeepLinkEventRequest` containing the result
   * if the deep link was handled.
   * Should be sent from mobile to web.
   */
  native_deep_link_event_response?:
    | TaxWebBridgeNativeDeepLinkEventResponse
    | undefined;
  /**
   * A response for `TaxWebBridgeDesktopButtonTapEventRequest `
   * Should be sent from web to mobile.
   */
  desktop_button_tap_event_response?: TaxWebBridgeDesktopButtonTapEventResponse | undefined;
}

export enum TaxWebBridgeResponseStatus {
  SUCCESS = "SUCCESS",
  INVALID_REQUEST = "INVALID_REQUEST",
}
